import { FieldArrayWithId, UseFormReturn } from 'react-hook-form';

import { Box, Flex } from '@chakra-ui/react';
import { BaseCard, createFieldHelper, HookFormInput } from '@invara-dev/react-base';
import { ReportingRequirementOptionInput } from './reportingRequirementOptionInput';
import {
  ActivityTravellerReportingRequirementViewModel,
  UpdateActivityReportingRequirementsModel,
  UpdateActivityTravellerReportingRequirementModel
} from '../../../../../features/activities/editActivity/editReportingRequirements/types';
import { ReportingRequirementViewModelDisplay } from '../../../../../features/activities/editActivity/editReportingRequirements/components/reportingRequirementViewModelDisplay';
import { ReportingValuePatternFailureExamples } from '../../../../../features/activities/editActivity/editReportingRequirements/components/reportingValuePatternFailureExamples';
import { isPreRecOrIssueRecStatus } from '../../../../../features/activities/editActivity/editReportingRequirements/utils/statusUtils';
import {
  getEffectiveReportingValueValidationPattern,
  getReportingValueRegexError
} from '../../../../../features/company/reportingRequirements/utils/reportingValueValidation';
import ActivityStatus from '../../../../../enums/activities/ActivityStatus.ts';

type Props = {
  fieldArray: FieldArrayWithId<UpdateActivityReportingRequirementsModel, 'TravellerReportingRequirements', 'id'>[];
  viewModels: ActivityTravellerReportingRequirementViewModel[];
  formMethods: UseFormReturn<UpdateActivityReportingRequirementsModel>;
  travellerName: string;
  travellerId: number;
  isDisabled: boolean;
  hasBookedHotel: boolean;
  activityStatus?: ActivityStatus;
  /** Number of columns used by the parent layout. When 2, the card fills its grid cell instead of flex-wrapping. */
  columns?: 1 | 2;
};

export const TravellerReportingRequirementsDisplay = (props: Props) => {
  const travellerFieldHelper = createFieldHelper<UpdateActivityTravellerReportingRequirementModel>(props.formMethods, {
    inputProps: {
      disabled: props.isDisabled
    }
  });

  const onOptionChange = (values: Array<string | undefined>, index: number) => {
    const newValue = values.join('-');
    if (newValue && newValue.length > 0) {
      props.formMethods.setValue(`TravellerReportingRequirements.${index}.Value`, newValue);
    }
  };

  const getInputDef = (model: ActivityTravellerReportingRequirementViewModel) => {
    // Allow editing static coding if activity is in Pre-Rec or Issue-Rec status
    const canEditStaticCoding = isPreRecOrIssueRecStatus(props.activityStatus);
    const isReadOnly = model.IsStaticCoding && !canEditStaticCoding;

    const pattern = getEffectiveReportingValueValidationPattern(model);
    return travellerFieldHelper
      .textInput('Value')
      .withInputProps({ readOnly: isReadOnly })
      .withValidation({
        required:
          props.hasBookedHotel && model.IsMandatory
            ? { message: 'Mandatory reporting requirements are required with a booked hotel.' }
            : undefined,
        validate: (v: string | undefined) => getReportingValueRegexError(v, pattern ?? null)
      });
  };

  if (props.fieldArray.length == 0) {
    return <></>;
  }

  const columns = props.columns ?? 1;

  return (
    <BaseCard
      header={props.travellerName}
      variant={'fieldArray'}
      w={'100%'}
      minW={columns === 2 ? undefined : '50%'}
      flex={columns === 2 ? undefined : 1}>
      <Flex
        flexDir={columns === 2 ? 'column' : 'row'}
        flexWrap={columns === 2 ? 'nowrap' : 'wrap'}
        gap={'4px'}
        w={'100%'}>
        {props.fieldArray
          .filter((val) => val.TravellerId == props.travellerId)
          .map((_field, fieldIndex) => {
            const viewModel = props.viewModels.find(
              (val) =>
                val.ReportingRequirementId == _field.ReportingRequirementId && val.TravellerId == _field.TravellerId
            );
            if (!viewModel) {
              return <Box key={fieldIndex}>Unable to find matching view model! Contact support.</Box>;
            }
            const index = props.fieldArray.indexOf(_field);
            const fieldArrayKey = `TravellerReportingRequirements.${index}.Value`;
            return (
              <Box
                key={fieldIndex}
                backgroundColor={'#e4e4e4'}
                padding={'4px'}
                width={columns === 2 ? '100%' : undefined}
                minWidth={columns === 2 ? undefined : '49%'}>
                <ReportingRequirementViewModelDisplay viewModel={viewModel} />
                <Flex direction={'column'}>
                  {viewModel.Options && viewModel.Options.length > 0 && (
                    <ReportingRequirementOptionInput
                      isDisabled={props.isDisabled}
                      formMethods={props.formMethods}
                      options={viewModel.Options}
                      fieldArrayIndex={index}
                      fieldArrayPath={'TravellerReportingRequirements'}
                      onChange={(val) => onOptionChange(val, index)}
                    />
                  )}
                  <Box w={'100%'}>
                    <HookFormInput inputDef={getInputDef(viewModel)} fieldArrayName={fieldArrayKey} />
                    <ReportingValuePatternFailureExamples
                      control={props.formMethods.control}
                      valueName={fieldArrayKey}
                      viewModel={viewModel}
                    />
                  </Box>
                </Flex>
              </Box>
            );
          })}
      </Flex>
    </BaseCard>
  );
};
